/**
 * "Your tasks" on the tech Today screen — the iPad Tasks sheet (Tasks Both
 * Ways slice 7, 10/28, pick 6A — https://claude.ai/artifact/H7gsi8ERjULUpGCpUMZzH8
 * "iPad (techs)").
 *
 * Big cards, 18px and up, for wrench people on a job site: the title, who it
 * is from, a big button that opens the job (or device, or customer), then
 * three answers — **Done**, **Waiting on…** and **Can't do**. One tap each;
 * nothing pings anyone's phone. A What's new note shows its green tag instead.
 * An ⓘ explains it with annotated pictures of this screen.
 *
 * Receiving only, like before: the tech→office "Something for the office…"
 * create action stays off (Corbin, 2026-07-07; pick 6A). Hidden entirely
 * when there are no tasks.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Ban, Check, Hourglass, ListTodo } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { firstName, waitedFor, type Task, type TaskRecord } from "@/lib/tasks";
import { useFinishTask } from "@/components/hand-back-dialog";
import { TaskDrawer } from "@/components/task-drawer";
import { TaskRecordChip } from "@/components/task-record-chip";
import { CantDoDialog } from "@/components/cant-do-dialog";
import { MadeByLine } from "@/components/made-by-line";
import { InfoButton } from "@/components/info-button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { FIELD_TASKS_HELP } from "@/components/field-tasks-help";

type FieldTask = Task & { record?: TaskRecord | null };

/** One-tap "who has the ball" answers on the iPad. */
export const WAITING_CHOICES = ["The customer", "The office", "A part", "The city"];

/** "From Regina · Today", "From the system", "From Regina · 3 days". Pure. */
export function fromLine(t: Pick<Task, "reporterName" | "createdAt" | "madeBy">, now = new Date()): string {
    const who = t.madeBy?.kind === "system" ? "the system" : t.reporterName ? firstName(t.reporterName) : "the office";
    const age = waitedFor(t.createdAt, now);
    const sameDay = new Date(t.createdAt).toDateString() === now.toDateString();
    return `From ${who} · ${sameDay ? "Today" : age}`;
}

/** Can this tech say "Can't do"? Only when someone else asked (it goes back to them). */
export function canSayCantDo(t: Pick<Task, "reporterUserId" | "whatsNewNoteId">, me: string | undefined): boolean {
    return Boolean(t.reporterUserId) && t.reporterUserId !== me && !t.whatsNewNoteId;
}

function WaitingDialog({ task, onClose, onDone }: { task: FieldTask | null; onClose: () => void; onDone: () => void }) {
    const [other, setOther] = useState("");
    const mut = useMutation({
        mutationFn: (on: string) => api.post(`/api/tasks/${task?.id}/waiting`, { on }),
        onSuccess: (_d, on) => {
            toast.success(`Parked. It waits on ${on.toLowerCase()}.`);
            setOther("");
            onDone();
            onClose();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't save. Try again."),
    });
    return (
        <Dialog open={!!task} onClose={onClose} title="Waiting on who?">
            <div className="space-y-3" data-testid="field-waiting-dialog">
                {task && <p className="text-lg font-medium">{task.displayTitle ?? task.title}</p>}
                <div className="grid grid-cols-2 gap-2">
                    {WAITING_CHOICES.map((c) => (
                        <button
                            key={c}
                            type="button"
                            disabled={mut.isPending}
                            onClick={() => mut.mutate(c)}
                            className="min-h-16 rounded-xl border-2 border-violet-200 bg-violet-50 px-4 text-left text-lg font-semibold text-violet-900 hover:border-violet-500 dark:border-violet-900 dark:bg-violet-950/50 dark:text-violet-100"
                        >
                            {c}
                        </button>
                    ))}
                </div>
                <div className="flex gap-2">
                    <Input
                        value={other}
                        onChange={(e) => setOther(e.target.value)}
                        placeholder="Someone else…"
                        maxLength={160}
                        className="h-14 text-lg"
                        aria-label="Waiting on someone else"
                    />
                    <button
                        type="button"
                        disabled={!other.trim() || mut.isPending}
                        onClick={() => mut.mutate(other.trim())}
                        className="h-14 rounded-xl bg-violet-700 px-5 text-lg font-semibold text-white disabled:opacity-50"
                    >
                        Park it
                    </button>
                </div>
            </div>
        </Dialog>
    );
}

function TaskBigCard({
    task,
    me,
    onOpen,
    onDone,
    onWaiting,
    onCantDo,
}: {
    task: FieldTask;
    me: string | undefined;
    onOpen: () => void;
    onDone: () => void;
    onWaiting: () => void;
    onCantDo: () => void;
}) {
    const cantDo = canSayCantDo(task, me);
    return (
        <li
            className={cn(
                "space-y-3 rounded-2xl border-2 bg-card p-4",
                task.priority === "urgent" ? "border-red-400 dark:border-red-700" : "border-border",
                task.madeBy?.kind === "system" && "border-dashed",
            )}
            data-testid="field-task-card"
        >
            <button type="button" onClick={onOpen} className="block w-full text-left">
                <span className="flex items-start gap-2">
                    {task.priority === "urgent" && <AlertTriangle className="mt-1 h-5 w-5 shrink-0 text-destructive" />}
                    <span className="text-xl font-bold leading-snug">{task.displayTitle ?? task.title}</span>
                </span>
                <span className="mt-1 block text-lg text-muted-foreground" data-testid="field-task-from">
                    {fromLine(task)}
                    {task.waitingOn ? ` · Waiting on ${task.waitingOn}` : ""}
                </span>
            </button>
            <MadeByLine madeBy={task.madeBy} className="text-base" />
            {task.record && <TaskRecordChip record={task.record} size="lg" className="w-full justify-center" />}
            {task.whatsNewNoteId ? (
                <button
                    type="button"
                    onClick={onOpen}
                    className="min-h-14 w-full rounded-xl bg-emerald-100 text-lg font-bold text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200"
                >
                    What&apos;s new: read it
                </button>
            ) : (
                <div className={cn("grid gap-2", cantDo ? "grid-cols-3" : "grid-cols-2")}>
                    <button
                        type="button"
                        onClick={onDone}
                        className="flex min-h-14 items-center justify-center gap-2 rounded-xl bg-emerald-700 text-lg font-bold text-white hover:bg-emerald-800"
                        data-testid="field-task-done"
                    >
                        <Check className="h-5 w-5" /> Done
                    </button>
                    <button
                        type="button"
                        onClick={onWaiting}
                        className="flex min-h-14 items-center justify-center gap-2 rounded-xl border-2 border-violet-300 text-lg font-bold text-violet-900 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-200"
                        data-testid="field-task-waiting"
                    >
                        <Hourglass className="h-5 w-5" /> Waiting on…
                    </button>
                    {cantDo && (
                        <button
                            type="button"
                            onClick={onCantDo}
                            className="flex min-h-14 items-center justify-center gap-2 rounded-xl border-2 border-red-300 text-lg font-bold text-red-800 hover:bg-red-50 dark:border-red-800 dark:text-red-300"
                            data-testid="field-task-cant-do"
                        >
                            <Ban className="h-5 w-5" /> Can&apos;t do
                        </button>
                    )}
                </div>
            )}
        </li>
    );
}

export function FieldTasksCard() {
    const qc = useQueryClient();
    const { user } = useAuth();
    const me = user?.id;
    const [openTaskId, setOpenTaskId] = useState<string | null>(null);
    const [waitingFor, setWaitingFor] = useState<FieldTask | null>(null);
    const [cantDoFor, setCantDoFor] = useState<FieldTask | null>(null);

    const tasksQ = useQuery({
        queryKey: ["tasks", "field-mine"],
        queryFn: () => api.get<{ tasks: FieldTask[] }>("/api/tasks?status=open"),
        refetchInterval: 60_000,
    });

    function refresh() {
        qc.invalidateQueries({ queryKey: ["tasks"] });
        qc.invalidateQueries({ queryKey: ["task-inbox"] });
    }

    // Done on a task the office gave you shows them a green check with your
    // one line; the hook asks for the line when the server needs it (#1645).
    const { finish, dialog: handBackDialog } = useFinishTask(refresh);

    const tasks = tasksQ.data?.tasks ?? [];

    return (
        <>
            {tasks.length > 0 && (
                <section className="space-y-3 rounded-2xl border bg-muted p-3" data-testid="field-tasks-sheet">
                    <div className="flex items-center gap-2 px-1">
                        <ListTodo className="h-5 w-5 text-primary" />
                        <h2 className="text-xl font-bold">Your tasks</h2>
                        <span className="rounded-full bg-primary px-2.5 py-0.5 text-base font-bold text-primary-foreground">
                            {tasks.length} for you
                        </span>
                        <InfoButton topic={FIELD_TASKS_HELP.sheet} className="ml-auto" />
                    </div>
                    <ul className="space-y-3">
                        {tasks.map((t) => (
                            <TaskBigCard
                                key={t.id}
                                task={t}
                                me={me}
                                onOpen={() => setOpenTaskId(t.id)}
                                onDone={() =>
                                    finish({ id: t.id, title: t.displayTitle ?? t.title, askerName: t.reporterName })
                                }
                                onWaiting={() => setWaitingFor(t)}
                                onCantDo={() => setCantDoFor(t)}
                            />
                        ))}
                    </ul>
                </section>
            )}

            <TaskDrawer taskId={openTaskId} onClose={() => setOpenTaskId(null)} />
            {handBackDialog}
            <WaitingDialog task={waitingFor} onClose={() => setWaitingFor(null)} onDone={refresh} />
            <CantDoDialog
                open={!!cantDoFor}
                taskId={cantDoFor?.id ?? null}
                taskTitle={cantDoFor ? (cantDoFor.displayTitle ?? cantDoFor.title) : null}
                askerName={cantDoFor?.reporterName ?? null}
                big
                onDone={refresh}
                onClose={() => setCantDoFor(null)}
            />
        </>
    );
}
